import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Wallet, Plus, Trash2, Tag, ListChecks, ChevronUp, ChevronDown, ReceiptText,
  Bold, Minus, Eye, EyeOff, Type, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, Grid3x3, Pencil, RotateCcw, WrapText, MoveVertical,
} from "lucide-react";
import { DEFAULT_FEE_HEADS, DEFAULT_DISCOUNT_HEADS, DEFAULT_RECEIPT_FIELDS, DEFAULT_FEE_SECTION_ORDER } from "@/lib/feeHeads";
import { toast } from "sonner";

const CANVAS_W = 595, CANVAS_H = 842;
const FONT_SIZES = [7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28];
const FONT_FACES = ["Helvetica", "Times", "Courier"];
const FACE_CSS = { Helvetica: "Helvetica, Arial, sans-serif", Times: 'Georgia, "Times New Roman", serif', Courier: '"Courier New", monospace' };
const HEADER_KEYS = ["logo", "school_name", "address", "contact", "footer"];
const FIELD_GAP = { logo: 56, school_name: 32, contact: 38, fee_breakdown: 112, footer: 34 };
const fieldGap = (key) => FIELD_GAP[key] || 26;
const CONTENT_KEY = { school_name: "school_name", address: "address", footer: "receipt_footer", signature: "authorized_signatory" };

const todayStr = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const isCustom = (rf) => String(rf.key).startsWith("custom_");
const headingOf = (rf) => !!rf.heading || rf.key === "school_name";
const sizeOf = (rf) => rf.font_size || (rf.key === "school_name" ? 18 : 10);
const faceOf = (rf) => rf.font_face || "Helvetica";

export default function FeeSettings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [guides, setGuides] = useState({ v: [], h: [] });
  const [showGrid, setShowGrid] = useState(true);
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const resize = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r.data && r.data.school_name ? { ...r.data } : { allow_partial: true };
      if (!d.fee_heads || !d.fee_heads.length) d.fee_heads = [...DEFAULT_FEE_HEADS];
      d.discount_heads = (!d.discount_heads || !d.discount_heads.length)
        ? DEFAULT_DISCOUNT_HEADS.map((x) => ({ ...x }))
        : d.discount_heads.map((h) => (typeof h === "string" ? { name: h, percent: 0, fixed: 0 } : { name: h.name || "", percent: h.percent || 0, fixed: h.fixed || 0 }));
      if (!d.receipt_fields || !d.receipt_fields.length) d.receipt_fields = DEFAULT_RECEIPT_FIELDS.map((x) => ({ ...x }));
      let yy = 20;
      d.receipt_fields = d.receipt_fields.map((rf) => {
        const nf = { ...rf };
        if (nf.x == null) nf.x = HEADER_KEYS.includes(nf.key) ? 130 : 24;
        if (nf.y == null) nf.y = yy;
        yy += fieldGap(nf.key);
        return nf;
      });
      d.fee_section_order = computeOrder(d.fee_section_order);
      setF(d);
    });
  }, []);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const updHead = (key, idx, val) => setF((prev) => ({ ...prev, [key]: prev[key].map((x, i) => (i === idx ? val : x)) }));
  const delHead = (key, idx) => setF((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  const addHead = (key) => setF((prev) => ({ ...prev, [key]: [...(prev[key] || []), ""] }));
  const updDiscHead = (idx, patch) => setF((prev) => ({ ...prev, discount_heads: prev.discount_heads.map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));
  const addDiscHead = () => setF((prev) => ({ ...prev, discount_heads: [...(prev.discount_heads || []), { name: "", percent: 0, fixed: 0 }] }));

  const updRF = (idx, patch) => setF((prev) => ({ ...prev, receipt_fields: prev.receipt_fields.map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));
  const delRF = (idx) => setF((prev) => ({ ...prev, receipt_fields: prev.receipt_fields.filter((_, i) => i !== idx) }));
  const addRF = () => setF((prev) => ({ ...prev, receipt_fields: [...prev.receipt_fields, { key: `custom_${Date.now()}`, label: "New Field", value: "", enabled: true, x: 24, y: 40, font_size: 10, font_face: "Helvetica", color: "#000000" }] }));
  const actToggle = () => { if (sel != null) updRF(sel, { enabled: !f.receipt_fields[sel].enabled }); };
  const actDelete = () => { if (sel != null) { delRF(sel); setSel(null); } };
  const actAddLine = () => setF((prev) => ({ ...prev, receipt_fields: [...prev.receipt_fields, { key: `line_${Date.now()}`, type: "line", enabled: true, x: 24, y: 40, width: 360, color: "#333333" }] }));

  const onDown = (e, idx) => {
    if (editing === idx) return;
    setSel(idx);
    const rect = canvasRef.current.getBoundingClientRect();
    const rf = f.receipt_fields[idx];
    const _ps = 1;
    drag.current = { idx, offX: (e.clientX - rect.left) / _ps - (rf.x || 0), offY: (e.clientY - rect.top) / _ps - (rf.y || 0) };
    e.preventDefault();
  };
  const onCanvasMove = (e) => {
    const _ps = 1;
    const rz = resize.current;
    if (rz) {
      const dx = (e.clientX - rz.startX) / _ps;
      if (rz.kind === "logo") {
        updRF(rz.idx, { font_size: Math.max(6, Math.min(60, Math.round(rz.startVal + dx / 3))) });
      } else if (rz.kind === "text") {
        const dy = (e.clientY - rz.startY) / _ps;
        updRF(rz.idx, { width: Math.max(40, Math.min(CANVAS_W, Math.round(rz.startW + dx))), height: Math.max(16, Math.round(rz.startH + dy)) });
      } else {
        const delta = rz.vert ? ((e.clientY - rz.startY) / _ps) : dx;
        updRF(rz.idx, { width: Math.max(20, Math.min(CANVAS_W, Math.round(rz.startVal + delta))) });
      }
      return;
    }
    const d = drag.current;
    if (!d) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let nx = Math.max(0, Math.min(CANVAS_W - 6, (e.clientX - rect.left) / _ps - d.offX));
    let ny = Math.max(0, Math.min(CANVAS_H - 6, (e.clientY - rect.top) / _ps - d.offY));
    const el = canvasRef.current.querySelector(`[data-testid="rc-el-${d.idx}"]`);
    const ew = el ? el.offsetWidth : 0;
    const SNAP = 6;
    const vT = [24, CANVAS_W / 2, CANVAS_W - 24];
    const hT = [24, CANVAS_H / 2];
    (f.receipt_fields || []).forEach((rf, i) => {
      if (i === d.idx || rf.x == null) return;
      const oel = canvasRef.current.querySelector(`[data-testid="rc-el-${i}"]`);
      const ow = oel ? oel.offsetWidth : 0;
      vT.push(rf.x, rf.x + ow / 2, rf.x + ow);
      hT.push(rf.y);
    });
    const gv = [], gh = [];
    for (const t of vT) {
      if (Math.abs(nx - t) <= SNAP) { nx = t; gv.push(t); break; }
      if (Math.abs(nx + ew / 2 - t) <= SNAP) { nx = t - ew / 2; gv.push(t); break; }
      if (Math.abs(nx + ew - t) <= SNAP) { nx = t - ew; gv.push(t); break; }
    }
    for (const t of hT) {
      if (Math.abs(ny - t) <= SNAP) { ny = t; gh.push(t); break; }
    }
    setGuides({ v: gv, h: gh });
    updRF(d.idx, { x: Math.round(nx), y: Math.round(ny) });
  };
  const onCanvasUp = () => { drag.current = null; resize.current = null; setGuides({ v: [], h: [] }); };

  const alignSel = (dir) => {
    if (sel == null) return;
    const el = canvasRef.current?.querySelector(`[data-testid="rc-el-${sel}"]`);
    const w = el ? el.offsetWidth : 0;
    let x = 24;
    if (dir === "center") x = Math.max(0, Math.round((CANVAS_W - w) / 2));
    else if (dir === "right") x = Math.max(0, Math.round(CANVAS_W - w - 24));
    updRF(sel, { x });
  };

  const resetLayout = () => {
    if (!window.confirm("Reset the receipt to the default layout? Current field positions and any custom text fields / lines will be replaced.")) return;
    const fields = DEFAULT_RECEIPT_FIELDS.map((x) => ({ ...x }));
    setF((prev) => ({ ...prev, receipt_fields: fields }));
    setSel(null); setEditing(null);
    toast.info("Layout reset to default. Click Save Settings to keep it.");
  };

  const startEdit = (idx) => { setSel(idx); setEditing(idx); };

  const onResizeDown = (e, idx, kind) => {
    e.stopPropagation();
    e.preventDefault();
    setSel(idx);
    const rf = f.receipt_fields[idx];
    if (kind === "text") {
      const el = canvasRef.current?.querySelector(`[data-testid="rc-el-${idx}"]`);
      resize.current = { idx, kind, startX: e.clientX, startY: e.clientY,
        startW: rf.width || (el ? el.offsetWidth : 120), startH: rf.height || (el ? el.offsetHeight : 20) };
    } else {
      resize.current = { idx, kind, vert: kind === "line" && rf.orientation === "v", startX: e.clientX, startY: e.clientY, startVal: kind === "logo" ? (rf.font_size || 16) : (rf.width || 360) };
    }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 2_000_000) { toast.error("Logo too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    const clean = (arr) => (arr || []).map((x) => (x || "").trim()).filter(Boolean);
    const cleanDisc = (arr) => (arr || []).map((h) => ({ name: (h.name || "").trim(), percent: Number(h.percent) || 0, fixed: Number(h.fixed) || 0 })).filter((h) => h.name);
    const payload = {
      ...f, fee_heads: clean(f.fee_heads), discount_heads: cleanDisc(f.discount_heads),
      receipt_fields: (f.receipt_fields || []).filter((x) => x.type === "line" || (x.label || "").trim()),
    };
    setSaving(true);
    try { await api.put("/settings", payload); toast.success("Fee settings saved"); setF(payload); }
    catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!f) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const card = "bg-white rounded-xl border border-slate-200 p-6";
  const selF = sel != null ? (f.receipt_fields || [])[sel] : null;
  const noSel = sel == null;
  const CONTENT_ONLY = ["school_name", "address", "contact", "footer", "signature"];
  const canNewline = !!selF && !selF.type && selF.key !== "logo" && selF.key !== "fee_breakdown" && !CONTENT_ONLY.includes(selF.key);

  const discountHeadList = () => (
    <>
      <div className="hidden sm:grid grid-cols-[1fr_120px_120px_40px] gap-2 px-1 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">
        <span>Discount Head Name</span><span>% Discount</span><span>Fixed Discount</span><span></span>
      </div>
      <div className="space-y-2">
        {(f.discount_heads || []).map((h, idx) => (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_40px] gap-2 items-center">
            <Input value={h.name || ""} onChange={(e) => updDiscHead(idx, { name: e.target.value })} placeholder="Head name" data-testid={`discount-head-input-${idx}`} />
            <Input type="number" min="0" value={h.percent ?? ""} onChange={(e) => updDiscHead(idx, { percent: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="%" data-testid={`discount-head-percent-${idx}`} />
            <Input type="number" min="0" value={h.fixed ?? ""} onChange={(e) => updDiscHead(idx, { fixed: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="₹" data-testid={`discount-head-fixed-${idx}`} />
            <Button size="icon" variant="ghost" className="text-rose-500 shrink-0" onClick={() => delHead("discount_heads", idx)} data-testid={`discount-head-delete-${idx}`}><Trash2 size={16} /></Button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={addDiscHead} data-testid="discount-head-add"><Plus size={14} className="mr-1" />Add Discount Head</Button>
    </>
  );

  const headList = (listKey, testPrefix, addLabel) => (
    <>
      <div className="grid sm:grid-cols-2 gap-2">
        {(f[listKey] || []).map((name, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input value={name} onChange={(e) => updHead(listKey, idx, e.target.value)} data-testid={`${testPrefix}-input-${idx}`} placeholder="Head name" />
            <Button size="icon" variant="ghost" className="text-rose-500 shrink-0" onClick={() => delHead(listKey, idx)} data-testid={`${testPrefix}-delete-${idx}`}><Trash2 size={16} /></Button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => addHead(listKey)} data-testid={`${testPrefix}-add`}><Plus size={14} className="mr-1" />{addLabel}</Button>
    </>
  );

  const elText = (rf) => {
    const lbl = rf.label || rf.key;
    const sep = rf.value_newline ? "\n" : " ";
    switch (rf.key) {
      case "school_name": return f.school_name || "";
      case "address": return f.address || "";
      case "contact": return `Ph: ${f.school_number || ""}  ${f.email || ""}`;
      case "footer": return f.receipt_footer || "";
      case "signature": return f.authorized_signatory || "Authorized Signatory";
      case "receipt_no": return `${lbl}:${sep}${(f.receipt_prefix || "RCPT")}-2026-00042`;
      case "date": return `${lbl}:${sep}${todayStr()}`;
      case "student_name": return `${lbl}:${sep}Aarav Mehta`;
      case "father_name": return `${lbl}:${sep}Rajesh Mehta`;
      case "class": return `${lbl}:${sep}5th - A`;
      case "admission_no": return `${lbl}:${sep}ADM001`;
      case "roll_no": return `${lbl}:${sep}12`;
      case "amount_paid": return `${lbl}:${sep}Rs. 6,000.00`;
      case "discount": return `${lbl}:${sep}Rs. 0.00`;
      case "due": return `${lbl}:${sep}Rs. 0.00`;
      case "fee_breakdown": {
        const rows = [["Tuition Fee", "Rs. 3,000.00"], ["Admission Fee", "Rs. 5,000.00"]];
        return (
          <div style={{ minWidth: 220 }}>
            <div style={{ fontWeight: 700 }}>{lbl}:</div>
            {rows.map(([n, a]) => (
              <div key={n} style={{ display: "flex", justifyContent: "space-between", gap: 16, borderBottom: "1px dotted #b8b8b8", padding: "1px 0" }}>
                <span>{n}</span><span>{a}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontWeight: 700, borderTop: "1px solid currentColor", padding: "1px 0" }}>
              <span>Total</span><span>Rs. 8,000.00</span>
            </div>
            <div style={{ fontSize: "0.85em", marginTop: 2 }}>For: Apr 2026, May 2026</div>
          </div>
        );
      }
      default: return `${lbl}:${sep}${rf.value || ""}`;
    }
  };

  const editValue = (rf) => (CONTENT_KEY[rf.key] ? (f[CONTENT_KEY[rf.key]] || "") : (isCustom(rf) ? (rf.value || "") : (rf.label || "")));
  const commitEdit = (rf, idx, val) => {
    if (CONTENT_KEY[rf.key]) set(CONTENT_KEY[rf.key], val);
    else if (isCustom(rf)) updRF(idx, { value: val });
    else updRF(idx, { label: val });
  };

  const rcElement = (rf, idx) => {
    const common = {
      position: "absolute", left: rf.x || 0, top: rf.y || 0,
      cursor: drag.current ? "grabbing" : "grab", userSelect: "none",
    };
    if (!rf.enabled) return null;
    const ring = sel === idx ? "outline outline-2 outline-indigo-400" : "hover:outline hover:outline-1 hover:outline-slate-300";
    if (rf.type === "line") {
      const vert = rf.orientation === "v";
      const len = rf.width || 360;
      return (
        <div key={rf.key} onMouseDown={(e) => onDown(e, idx)} data-testid={`rc-el-${idx}`}
          style={{ ...common, width: vert ? 8 : len, height: vert ? len : 8 }}
          className={`rounded ${ring} ${!rf.enabled ? "opacity-40" : ""}`}>
          <div style={vert ? { borderLeft: `1px solid ${rf.color || "#333"}`, marginLeft: 4, height: "100%" } : { borderTop: `1px solid ${rf.color || "#333"}`, marginTop: 4 }} />
          {sel === idx && (
            <span onMouseDown={(e) => onResizeDown(e, idx, "line")} data-testid={`rc-resize-${idx}`}
              style={vert ? { position: "absolute", left: -2, bottom: -5 } : { position: "absolute", right: -5, top: -2 }}
              className={`w-3 h-3 bg-indigo-500 border border-white rounded-sm ${vert ? "cursor-ns-resize" : "cursor-ew-resize"}`} />
          )}
        </div>
      );
    }
    if (rf.key === "logo") {
      if (!f.logo) return null;
      const dim = rf.font_size ? rf.font_size * 3 : 48;
      return (
        <div key={rf.key} onMouseDown={(e) => onDown(e, idx)} data-testid={`rc-el-${idx}`}
          style={{ ...common }} className={`rounded ${ring}`}>
          <img src={f.logo} alt="logo" style={{ height: dim, width: dim, objectFit: "contain", pointerEvents: "none" }} />
          {sel === idx && (
            <span onMouseDown={(e) => onResizeDown(e, idx, "logo")} data-testid={`rc-resize-${idx}`}
              style={{ position: "absolute", right: -5, bottom: -5 }}
              className="w-3 h-3 bg-indigo-500 border border-white rounded-sm cursor-se-resize" />
          )}
        </div>
      );
    }
    const style = {
      ...common, fontSize: sizeOf(rf), fontFamily: FACE_CSS[faceOf(rf)],
      fontWeight: headingOf(rf) ? 700 : 400, color: rf.color || "#111",
      whiteSpace: rf.width ? "pre-wrap" : "pre", lineHeight: 1.2, wordBreak: "break-word",
      ...(rf.width ? { width: rf.width } : {}),
      ...(rf.height ? { height: rf.height, overflow: "hidden" } : {}),
    };
    return (
      <div key={rf.key} onMouseDown={(e) => onDown(e, idx)} onDoubleClick={() => setEditing(idx)}
        data-testid={`rc-el-${idx}`} style={style} className={`rounded px-0.5 ${ring} ${!rf.enabled ? "opacity-40" : ""}`}>
        {editing === idx ? (
          <textarea autoFocus defaultValue={editValue(rf)} onMouseDown={(e) => e.stopPropagation()}
            onBlur={(e) => { commitEdit(rf, idx, e.target.value); setEditing(null); }}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Escape") { commitEdit(rf, idx, e.target.value); setEditing(null); } }}
            rows={Math.max(1, editValue(rf).split("\n").length)}
            className="bg-yellow-50 outline-none rounded px-0.5 resize-none overflow-hidden"
            style={{ fontSize: sizeOf(rf), fontFamily: FACE_CSS[faceOf(rf)], lineHeight: 1.2, whiteSpace: "pre",
              width: rf.width ? rf.width : `${Math.max(8, Math.max(...editValue(rf).split("\n").map((l) => l.length), 8))}ch` }}
            data-testid={`rc-edit-${idx}`} />
        ) : elText(rf)}
        {sel === idx && editing !== idx && (
          <span onMouseDown={(e) => onResizeDown(e, idx, "text")} data-testid={`rc-resize-${idx}`}
            style={{ position: "absolute", right: -5, bottom: -5 }}
            className="w-3 h-3 bg-indigo-500 border border-white rounded-sm cursor-se-resize" />
        )}
      </div>
    );
  };

  const rcStatic = (rf) => {
    if (!rf.enabled) return null;
    const common = { position: "absolute", left: rf.x || 0, top: rf.y || 0 };
    if (rf.type === "line") {
      const vert = rf.orientation === "v"; const len = rf.width || 360;
      return <div key={rf.key} style={{ ...common, width: vert ? 8 : len, height: vert ? len : 8 }}><div style={vert ? { borderLeft: `1px solid ${rf.color || "#333"}`, marginLeft: 4, height: "100%" } : { borderTop: `1px solid ${rf.color || "#333"}`, marginTop: 4 }} /></div>;
    }
    if (rf.key === "logo") {
      if (!f.logo) return null;
      const dim = rf.font_size ? rf.font_size * 3 : 48;
      return <div key={rf.key} style={common}><img src={f.logo} alt="logo" style={{ height: dim, width: dim, objectFit: "contain" }} /></div>;
    }
    const style = { ...common, fontSize: sizeOf(rf), fontFamily: FACE_CSS[faceOf(rf)], fontWeight: headingOf(rf) ? 700 : 400, color: rf.color || "#111", whiteSpace: rf.width ? "pre-wrap" : "pre", lineHeight: 1.2, wordBreak: "break-word", ...(rf.width ? { width: rf.width } : {}), ...(rf.height ? { height: rf.height, overflow: "hidden" } : {}) };
    return <div key={rf.key} style={style}>{elText(rf)}</div>;
  };

  const rcCopies = Math.max(1, Math.min(2, f.receipt_copies || 1));
  const COPY_LABELS = ["Office Copy", "Student Copy", "Bank Copy"];

  const fieldLabel = (rf) => rf.type === "line" ? "── Divider Line" : (rf.key === "logo" ? "School Logo" : (rf.label || rf.key));

  const receiptDesignBody = () => (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="lg:w-56 shrink-0" data-testid="receipt-field-list">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Receipt Fields</div>
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[540px] overflow-y-auto">
          {(f.receipt_fields || []).map((rf, idx) => (
            <div key={rf.key} onClick={() => setSel(idx)}
              className={`flex items-center justify-between gap-1 px-2.5 py-2 cursor-pointer text-sm ${sel === idx ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-slate-50 text-slate-700"} ${!rf.enabled ? "opacity-50" : ""}`}
              data-testid={`field-row-${idx}`}>
              <span className="truncate flex-1">{fieldLabel(rf)}</span>
              {rf.type !== "line" && rf.key !== "logo" && (
                <button type="button" onClick={(e) => { e.stopPropagation(); startEdit(idx); }}
                  className="shrink-0 text-slate-400 hover:text-indigo-600" title="Edit text" data-testid={`field-edit-${idx}`}>
                  <Pencil size={13} />
                </button>
              )}
              <button type="button" onClick={(e) => { e.stopPropagation(); updRF(idx, { enabled: !rf.enabled }); }}
                className="shrink-0 text-slate-400 hover:text-indigo-600" title="Show / hide" data-testid={`field-toggle-${idx}`}>
                {rf.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 min-w-0">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 mb-4 flex items-center gap-1.5 flex-wrap" data-testid="receipt-toolbar">
        <span className="text-xs text-slate-500 mr-1 min-w-[120px]">{selF ? `Editing: ${selF.label || selF.key}` : "Click an element to select"}</span>
        <select disabled={noSel} value={selF ? sizeOf(selF) : 10} onChange={(e) => updRF(sel, { font_size: Number(e.target.value) })}
          className="h-8 rounded border border-slate-200 bg-white text-sm px-1 disabled:opacity-40" data-testid="tb-font-size" title="Font size">
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s}px</option>)}
        </select>
        <select disabled={noSel} value={selF ? faceOf(selF) : "Helvetica"} onChange={(e) => updRF(sel, { font_face: e.target.value })}
          className="h-8 rounded border border-slate-200 bg-white text-sm px-1 disabled:opacity-40" data-testid="tb-font-face" title="Font face">
          {FONT_FACES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="color" disabled={noSel} value={(selF && selF.color) || "#111111"} onChange={(e) => updRF(sel, { color: e.target.value })}
          className="h-8 w-9 rounded border border-slate-200 bg-white disabled:opacity-40 cursor-pointer" data-testid="tb-color" title="Text color" />
        <button type="button" disabled={noSel} onClick={() => updRF(sel, { heading: !headingOf(selF) })} title="Bold"
          className={`p-1.5 rounded border ${selF && headingOf(selF) ? "bg-indigo-100 text-indigo-600 border-indigo-200" : "text-slate-500 border-slate-200 bg-white hover:bg-slate-100"} disabled:opacity-30`} data-testid="tb-bold"><Bold size={15} /></button>
        <button type="button" disabled={!canNewline} onClick={() => updRF(sel, { value_newline: !selF.value_newline })} title="Put value on a new line"
          className={`p-1.5 rounded border ${selF && selF.value_newline ? "bg-indigo-100 text-indigo-600 border-indigo-200" : "text-slate-500 border-slate-200 bg-white hover:bg-slate-100"} disabled:opacity-30`} data-testid="tb-newline"><WrapText size={15} /></button>
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <button type="button" disabled={noSel} onClick={() => alignSel("left")} title="Align left"
          className="p-1.5 rounded border text-slate-500 border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30" data-testid="tb-align-left"><AlignLeft size={15} /></button>
        <button type="button" disabled={noSel} onClick={() => alignSel("center")} title="Align center"
          className="p-1.5 rounded border text-slate-500 border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30" data-testid="tb-align-center"><AlignCenter size={15} /></button>
        <button type="button" disabled={noSel} onClick={() => alignSel("right")} title="Align right"
          className="p-1.5 rounded border text-slate-500 border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30" data-testid="tb-align-right"><AlignRight size={15} /></button>
        <button type="button" onClick={() => setShowGrid((v) => !v)} title="Toggle gridlines"
          className={`p-1.5 rounded border ${showGrid ? "bg-indigo-100 text-indigo-600 border-indigo-200" : "text-slate-500 border-slate-200 bg-white hover:bg-slate-100"}`} data-testid="tb-grid"><Grid3x3 size={15} /></button>
        {selF && selF.type === "line" && (
          <button type="button" onClick={() => updRF(sel, { orientation: selF.orientation === "v" ? "h" : "v" })} title="Rotate line (horizontal / vertical)"
            className={`p-1.5 rounded border ${selF.orientation === "v" ? "bg-indigo-100 text-indigo-600 border-indigo-200" : "text-slate-500 border-slate-200 bg-white hover:bg-slate-100"}`} data-testid="tb-rotate-line"><MoveVertical size={15} /></button>
        )}
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <button type="button" disabled={noSel} onClick={actToggle} title="Show / hide"
          className="p-1.5 rounded border text-slate-500 border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30" data-testid="tb-toggle">{selF && selF.enabled ? <EyeOff size={15} /> : <Eye size={15} />}</button>
        <button type="button" disabled={noSel} onClick={actDelete} title="Delete"
          className="p-1.5 rounded border text-rose-500 border-slate-200 bg-white hover:bg-rose-50 disabled:opacity-30" data-testid="tb-delete"><Trash2 size={15} /></button>
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <button type="button" onClick={actAddLine} className="px-2 py-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 text-xs flex items-center gap-1" data-testid="tb-add-line"><Minus size={14} />Add Line</button>
        <button type="button" onClick={addRF} className="px-2 py-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 text-xs flex items-center gap-1" data-testid="tb-add-field"><Type size={14} />Add Text</button>
        <button type="button" onClick={resetLayout} className="px-2 py-1.5 rounded border border-amber-200 bg-white text-amber-600 hover:bg-amber-50 text-xs flex items-center gap-1" data-testid="tb-reset-layout"><RotateCcw size={14} />Reset Layout</button>
      </div>

      <div className="flex items-center gap-2 mb-3 text-sm flex-wrap" data-testid="logo-upload-row">
        <span className="text-slate-500">School Logo:</span>
        {f.logo ? <img src={f.logo} alt="logo" className="h-9 w-9 object-contain border border-slate-200 rounded bg-white" data-testid="logo-preview" />
          : <span className="text-xs text-slate-400">No logo uploaded</span>}
        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="logo-upload-input" />
        <button type="button" onClick={() => logoInputRef.current?.click()} className="px-2 py-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 text-xs flex items-center gap-1" data-testid="logo-upload-btn"><ImageIcon size={14} />{f.logo ? "Change Logo" : "Upload Logo"}</button>
        {f.logo && <button type="button" onClick={() => set("logo", "")} className="px-2 py-1.5 rounded border border-slate-200 bg-white text-rose-500 hover:bg-rose-50 text-xs flex items-center gap-1" data-testid="logo-remove-btn"><Trash2 size={14} />Remove</button>}
        <span className="text-xs text-slate-400">Drag the logo's corner handle on the canvas to resize.</span>
      </div>

      <div className="flex items-center gap-2 mb-3 text-sm flex-wrap" data-testid="receipt-copies-row">
        <span className="text-slate-500">Receipts per page:</span>
        {[1, 2].map((n) => (
          <button key={n} type="button" onClick={() => set("receipt_copies", n)}
            className={`px-3 py-1.5 rounded border text-xs ${(f.receipt_copies || 1) === n ? "bg-indigo-100 text-indigo-600 border-indigo-200" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}
            data-testid={`receipt-copies-${n}`}>{n === 1 ? "One Receipt" : "Two Receipts"}</button>
        ))}
        <span className="text-xs text-slate-400">Chosen number of copies will print stacked on one A4 page.</span>
      </div>

      <div className="overflow-auto max-w-full">
        <div data-testid="receipt-page"
          style={{ width: CANVAS_W, height: CANVAS_H, position: "relative",
            backgroundImage: showGrid ? "linear-gradient(#eef2ff 1px, transparent 1px), linear-gradient(90deg, #eef2ff 1px, transparent 1px)" : undefined,
            backgroundSize: "20px 20px" }}
          className="border-2 border-slate-300 bg-white shadow-sm rounded overflow-hidden">
          {Array.from({ length: rcCopies }).map((_, ci) => {
            const isEdit = ci === 0;
            const bandTop = ci * (CANVAS_H / rcCopies);
            return (
              <div key={ci} style={{ position: "absolute", top: bandTop, left: 0, width: CANVAS_W, height: CANVAS_H / rcCopies, overflow: "hidden" }}>
                {isEdit ? (
                  <div ref={canvasRef} onMouseMove={onCanvasMove} onMouseUp={onCanvasUp} onMouseLeave={onCanvasUp}
                    data-testid="receipt-canvas" style={{ width: CANVAS_W, height: CANVAS_H, position: "relative" }}>
                    {(f.receipt_fields || []).map((rf, idx) => rcElement(rf, idx))}
                    {guides.v.map((gx, i) => <div key={`gv${i}`} style={{ position: "absolute", left: gx, top: 0, bottom: 0, width: 1, background: "#6366f1", pointerEvents: "none" }} />)}
                    {guides.h.map((gy, i) => <div key={`gh${i}`} style={{ position: "absolute", top: gy, left: 0, right: 0, height: 1, background: "#6366f1", pointerEvents: "none" }} />)}
                  </div>
                ) : (
                  <div data-testid={`receipt-copy-${ci}`} style={{ width: CANVAS_W, height: CANVAS_H, position: "relative", pointerEvents: "none" }}>
                    {(f.receipt_fields || []).map((rf) => rcStatic(rf))}
                  </div>
                )}
                {rcCopies > 1 && <div style={{ position: "absolute", top: 6, right: 12, fontSize: 11, fontWeight: 700 }} className="text-slate-500">[ {COPY_LABELS[ci] || `Copy ${ci + 1}`} ]</div>}
                {ci > 0 && <div style={{ position: "absolute", top: 0, left: 0, right: 0, borderTop: "1px dashed #cbd5e1" }} />}
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">A4 size preview — drag any element to position it. Double-click text to edit. Use the toolbar for font size, face, colour &amp; bold. This layout is exactly what prints at fee collection.</p>
      </div>
    </div>
  );

  const receiptDueBody = () => (
    <div className="grid sm:grid-cols-2 gap-4">
      <div><Label>Receipt Prefix</Label><Input value={f.receipt_prefix || ""} onChange={(e) => set("receipt_prefix", e.target.value)} data-testid="fee-setting-receipt_prefix" /></div>
      <div><Label>Fee Due Date</Label><Input value={f.fee_due_date || ""} onChange={(e) => set("fee_due_date", e.target.value)} data-testid="fee-setting-fee_due_date" /></div>
      <div><Label>Late Fee</Label><Input type="number" value={f.late_fee || ""} onChange={(e) => set("late_fee", e.target.value)} data-testid="fee-setting-late_fee" /></div>
      <div><Label>Online Payment Link (for reminders)</Label><Input value={f.payment_link || ""} onChange={(e) => set("payment_link", e.target.value)} data-testid="fee-setting-payment_link" /></div>
      <div className="sm:col-span-2"><Label>Receipt Footer</Label><Input value={f.receipt_footer || ""} onChange={(e) => set("receipt_footer", e.target.value)} data-testid="fee-setting-receipt_footer" /></div>
    </div>
  );

  const reminderBody = () => (
    <>
      <Label>Reminder Message Template</Label>
      <textarea value={f.reminder_template || ""} onChange={(e) => set("reminder_template", e.target.value)} data-testid="fee-setting-reminder_template" rows={3}
        className="w-full mt-1 rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        placeholder="Dear Parent, a fee of {due} is pending for {name} ({admission_number}) at {school}. Please pay by {due_date}. Pay online: {payment_link}" />
      <p className="text-xs text-slate-400 mt-1">Placeholders: {"{name}"}, {"{admission_number}"}, {"{father_name}"}, {"{due}"}, {"{due_date}"}, {"{school}"}, {"{payment_link}"}. Leave blank for the default message.</p>
    </>
  );

  const paymentBody = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
        <div><div className="font-medium text-slate-800">Allow Partial Payments</div><div className="text-xs text-slate-400">Accept payments less than the full due amount</div></div>
        <Switch checked={!!f.allow_partial} onCheckedChange={(v) => set("allow_partial", v)} data-testid="fee-setting-partial" />
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
        <div><div className="font-medium text-slate-800">Allow Discounts</div><div className="text-xs text-slate-400">Show the discount field at collection & allow concessions</div></div>
        <Switch checked={f.allow_discount !== false} onCheckedChange={(v) => set("allow_discount", v)} data-testid="fee-setting-discount" />
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
        <div><div className="font-medium text-slate-800">Edit Date on Collect Fees</div><div className="text-xs text-slate-400">Allow changing the payment date &amp; time on the Collect Fees dialog box</div></div>
        <Switch checked={f.allow_edit_collect_date !== false} onCheckedChange={(v) => set("allow_edit_collect_date", v)} data-testid="fee-setting-edit-date" />
      </div>
      <div className="p-3 rounded-lg bg-slate-50 space-y-3">
        <div className="flex items-center justify-between">
          <div><div className="font-medium text-slate-800">Late Fine</div><div className="text-xs text-slate-400">Auto-add a fine for each overdue, unpaid month</div></div>
          <Switch checked={!!f.late_fine_enabled} onCheckedChange={(v) => set("late_fine_enabled", v)} data-testid="fee-setting-late-fine" />
        </div>
        {f.late_fine_enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Fine per overdue month (₹)</Label><Input type="number" value={f.late_fine_per_month || ""} onChange={(e) => set("late_fine_per_month", e.target.value)} data-testid="fee-setting-late-fine-amount" /></div>
            <div><Label>Applies from day of month</Label><Input type="number" value={f.late_fine_day || 11} onChange={(e) => set("late_fine_day", e.target.value)} data-testid="fee-setting-late-fine-day" /></div>
          </div>
        )}
      </div>
    </div>
  );

  const SECTIONS = {
    fee_heads: { title: "Fee Heads", subtitle: "Default fee heads used when creating a Fee Structure. Edit or delete as needed.", icon: ListChecks, color: "text-indigo-600", body: () => headList("fee_heads", "fee-head", "Add Fee Head") },
    discount_heads: { title: "Discount Heads", subtitle: "Discount / concession heads available while collecting fees.", icon: Tag, color: "text-emerald-600", body: () => discountHeadList() },
    receipt_design: { title: "Fee Receipt Design", subtitle: "Drag & drop layout — matches the printed receipt.", icon: ReceiptText, color: "text-violet-600", body: receiptDesignBody },
    receipt_due: { title: "Receipt & Due", subtitle: "", icon: Wallet, color: "text-slate-600", body: receiptDueBody },
    reminder: { title: "Reminder Message", subtitle: "", icon: Wallet, color: "text-slate-600", body: reminderBody },
    payment: { title: "Payment Options", subtitle: "", icon: Wallet, color: "text-slate-600", body: paymentBody },
  };
  const order = f.fee_section_order;

  return (
    <div className="space-y-6 w-full max-w-[95%] mx-auto" data-testid="fee-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600"><Wallet size={20} /></div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Fee Setting</h1>
          <p className="text-slate-500">Manage fee heads, discount heads, receipt design & payment options</p>
        </div>
      </div>

      {order.map((key, idx) => {
        const sec = SECTIONS[key];
        if (!sec) return null;
        const Icon = sec.icon;
        return (
          <section key={key} className={card} data-testid={`section-${key}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Icon size={18} className={sec.color} />
                <div>
                  <h3 className="font-heading font-bold text-slate-900">{sec.title}</h3>
                  {sec.subtitle && <p className="text-sm text-slate-500">{sec.subtitle}</p>}
                </div>
              </div>
              <div className="flex flex-col -space-y-1 shrink-0">
                <button type="button" disabled={idx === 0} onClick={() => setF((prev) => { const a = [...prev.fee_section_order]; [a[idx], a[idx - 1]] = [a[idx - 1], a[idx]]; return { ...prev, fee_section_order: a }; })} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`section-up-${key}`}><ChevronUp size={18} /></button>
                <button type="button" disabled={idx === order.length - 1} onClick={() => setF((prev) => { const a = [...prev.fee_section_order]; [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]]; return { ...prev, fee_section_order: a }; })} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`section-down-${key}`}><ChevronDown size={18} /></button>
              </div>
            </div>
            {sec.body()}
          </section>
        );
      })}

      <Button onClick={save} disabled={saving} data-testid="save-fee-settings-btn">
        {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}Save Fee Settings
      </Button>
    </div>
  );
}

function computeOrder(saved) {
  const base = DEFAULT_FEE_SECTION_ORDER;
  if (!saved || !saved.length) return [...base];
  const valid = saved.filter((k) => base.includes(k));
  const missing = base.filter((k) => !valid.includes(k));
  return [...valid, ...missing];
}
